import { Dialog, DynamicForm, DynamicFormRequiredType } from '@myosh/odin-components';
import type {
  DynamicFormButtonSetting,
  DynamicFormRef,
  DynamicFormSettings,
  JsonDataItem,
} from '@myosh/odin-components';
import i18next from '../../../i18n';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import {
  useCreateNewRiskLevelMutation,
  useDeleteRiskLevelMutation,
  useEditRiskLevelMutation,
} from '../../../redux/services/risk-matrix';
import { handleRiskMatrixApiResponse } from '../risk-rating/risk-rating-functions';
import type { RiskLevelsDataResponse } from '../../../@types/risk-matrix-field';
import { createCaptionObj } from '../../../utils/caption-util';
import { forceAssert } from '../../../utils/type-guards';
import { intNumberPattern } from '../../../utils/validation-utils';

interface RiskLevelModalProps {
  visible: boolean;
  riskMatrixId: number;
  selectedRiskLevel?: RiskLevelsDataResponse;
  handleModalClose: () => void;
}

const formSettings: DynamicFormSettings = {
  id: 0,
  caption: '',
  fields: [
    {
      id: 'caption',
      fieldType: 'TEXTFIELD',
      fieldName: 'caption',
      caption: i18next.t('name'),
      position: 0,
      required: DynamicFormRequiredType.True,
      permissions: { read: true, edit: true },
    },
    {
      id: 'descriptionCaption',
      fieldType: 'TEXTFIELD',
      fieldName: 'descriptionCaption',
      caption: i18next.t('description'),
      position: 1,
      required: DynamicFormRequiredType.True,
      permissions: { read: true, edit: true },
    },
    {
      id: 'backgroundColor',
      fieldType: 'TEXTFIELD',
      fieldName: 'backgroundColor',
      caption: i18next.t('color'),
      position: 2,
      dynamicProperties: {
        type: 'color',
        inputStyles: '!flex-none',
      },
      required: DynamicFormRequiredType.True,
      permissions: { read: true, edit: true },
    },
    {
      id: 'textColor',
      fieldType: 'TEXTFIELD',
      fieldName: 'textColor',
      caption: i18next.t('text-color'),
      position: 3,
      dynamicProperties: {
        type: 'color',
        inputStyles: '!flex-none',
      },
      required: DynamicFormRequiredType.True,
      permissions: { read: true, edit: true },
    },
    {
      id: 'category',
      fieldType: 'INTFIELD',
      fieldName: 'category',
      dynamicProperties: {
        validations: [
          {
            pattern: intNumberPattern,
            errorLabel: i18next.t('integer-validation-message'),
          },
        ],
      },
      caption: i18next.t('category'),
      position: 4,
      required: DynamicFormRequiredType.True,
      permissions: { read: true, edit: true },
    },
  ],
};

const RiskLevelModal = ({ selectedRiskLevel, riskMatrixId, visible, handleModalClose }: RiskLevelModalProps) => {
  const { t } = useTranslation();
  const dynamicFormReference = useRef<DynamicFormRef>(null);
  const dynamicFormId = useRef(v4());

  const [editRiskLevel] = useEditRiskLevelMutation();
  const [createNewRiskLevel] = useCreateNewRiskLevelMutation();
  const [deleteRiskLevel] = useDeleteRiskLevelMutation();

  const onFormSubmit = (data: JsonDataItem) => {
    const payload = {
      ...data,
      matrixId: riskMatrixId,
      caption: createCaptionObj(data.caption as string),
      descriptionCaption: createCaptionObj(data.descriptionCaption as string),
    };
    if (selectedRiskLevel) {
      handleRiskMatrixApiResponse(editRiskLevel(forceAssert(payload)).unwrap());
    } else {
      handleRiskMatrixApiResponse(createNewRiskLevel(forceAssert(payload)).unwrap());
    }
    handleModalClose();
  };

  const handleDelete = () => {
    if (selectedRiskLevel?.id) handleRiskMatrixApiResponse(deleteRiskLevel(selectedRiskLevel.id).unwrap());
    handleModalClose();
  };

  const [confirmationDialogButtons, setConfirmationDialogButtons] = useState<DynamicFormButtonSetting[]>([]);

  useEffect(() => {
    const baseButton: DynamicFormButtonSetting[] = [
      {
        name: 'save',
        text: t('save'),
        htmlType: 'submit',
        type: 'primary',
        variant: 'alternative',
      },
    ];

    const conditionalButtons: DynamicFormButtonSetting[] = selectedRiskLevel?.id
      ? [
          {
            name: 'delete',
            text: t('delete'),
            htmlType: 'button',
            type: 'default',
            variant: 'alternative',
            onClick: handleDelete,
          },
          {
            name: 'discard',
            text: t('discard'),
            htmlType: 'button',
            type: 'default',
            variant: 'alternative',
            onClick: handleModalClose,
          },
        ]
      : [];

    setConfirmationDialogButtons([...baseButton, ...conditionalButtons]);
  }, [selectedRiskLevel, handleModalClose]);

  const defaultRiskLevelData: JsonDataItem = {
    backgroundColor: '#ffffff',
    textColor: '#000000',
  };

  return (
    <Dialog
      header={`${selectedRiskLevel ? t('edit') : t('new')} ${t('risk-level')} ${selectedRiskLevel?.caption ? ' - ' + selectedRiskLevel?.caption : ''}`}
      visible={visible}
      onClose={handleModalClose}
      containerClassName="sm:w-96"
    >
      <DynamicForm
        ref={dynamicFormReference}
        dynamicFormId={dynamicFormId.current}
        data={{
          ...(selectedRiskLevel?.id ? selectedRiskLevel : defaultRiskLevelData),
        }}
        settings={formSettings}
        readOnly={false}
        buttons={confirmationDialogButtons}
        buttonsLocation={1}
        buttonsPosition={1}
        onSubmit={onFormSubmit}
        validationEnabled={true}
      />
    </Dialog>
  );
};

export default RiskLevelModal;
